import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Col,
  Dropdown,
  Flex,
  MenuProps,
  Row,
  Skeleton,
  Spin,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import useGetDataProject from '../hooks/use-get-data-project'
import useWindowSize from '../../../../../hooks/window-size-hook'
import { dateFormatShort } from '../../../../../utils/dateFormat'
import IconGlobalApp from '../../../../../utils/IconGlobalApp'
import { mapCommonIcons } from '../../../../../utils/mapCommonIcons'
import useControlProjectMemberManagement from '../modules/project-member/hooks/use-control-project-member-management'
import useDeleteProject from '../hooks/use-delete-project'
import modalConfirmDelete from '../../../../../utils/modal-confirm-delete'
import { QUAN_LY_GIAO_VIEC_PREFIX_URL } from '../../../constants/config.quan-ly-giao-viec'
import ModalEditProject from '../components/form/Modal.Edit.Project'
import useCheckRole from '../../../../list-role/hooks/use-check-role'
import useGetCurrentUser from '../../../../cau-hinh-thong-tin/modules/user/hooks/use-get-current-user'
import {
  setCurrentUser,
  setIsAdmin,
  setIsLeader,
  setProjectDetail,
} from '../stores/project-reducer'
import useProjectStore from '../project.store'
import {
  DetailMenuItemType,
  LIST_MENU_DETAIL_PROJECT_LEADER_ADMIN,
  LIST_MENU_DETAIL_PROJECT_MEMBER,
} from '../../../constants/list-menu.quan-ly-giao-viec'
import DetailMenuProject from '../components/layout/DetailMenu.Project'
import cutFromString from '../../../../../utils/cut-form-string'
import { PROJECT_DEPARTMENT_PREFIX_URL } from '../constants/config.project'

const { Title, Text } = Typography

interface IProps {
  children: ReactNode
}

const LayoutDetailProject = ({ children }: IProps) => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isSmallSize } = useWindowSize({ options: { needWindowSize: false } })

  const baseUrl = projectId
    ? cutFromString(pathname, projectId)
    : `${QUAN_LY_GIAO_VIEC_PREFIX_URL}${PROJECT_DEPARTMENT_PREFIX_URL}`

  const [openEdit, setOpenEdit] = useState(false)

  const { detail, getDetail, loading } = useGetDataProject()

  const { isAdmin, currentUser, members, isLeader } = useProjectStore()

  const detailMenuItems: DetailMenuItemType[] =
    isAdmin || isLeader
      ? LIST_MENU_DETAIL_PROJECT_LEADER_ADMIN
      : LIST_MENU_DETAIL_PROJECT_MEMBER

  const { loading: loadingCheckRole, handleCheckRole } = useCheckRole({
    code: 'admin-giao-viec',
    customRequestHandler: true,
    onFinish: (result) => setIsAdmin(result),
  })

  const { getCurrentUser, loading: loadingUser } = useGetCurrentUser({
    customRequestHandler: true,
    onFinish: (user) => setCurrentUser(user),
  })

  const { handleDelete } = useDeleteProject({
    onFinish: () => navigate(baseUrl),
  })

  const confirmDelete = () => {
    if (!detail) return
    modalConfirmDelete({
      itemName: detail.title || undefined,
      onOk: async () => handleDelete(detail.id),
    })
  }

  const {
    loading: loadingMember,
    memberButton,
    memberModal,
    getMembers,
  } = useControlProjectMemberManagement(projectId)

  const dateRender = [
    detail?.startDate ? dayjs(detail.startDate).format(dateFormatShort) : null,
    detail?.endDate ? dayjs(detail.endDate).format(dateFormatShort) : null,
  ]
    .filter(Boolean)
    .join(' - ')

  const subInfoRender =
    [detail?.department?.name, dateRender].filter(Boolean).join(': ') ||
    detail?.description

  const actionItems: MenuProps['items'] = [
    {
      key: '1',
      label: 'Chỉnh sửa',
      icon: <IconGlobalApp name={mapCommonIcons.edit} />,
      onClick: () => setOpenEdit(true),
    },
    {
      key: '2',
      label: 'Xóa',
      danger: true,
      icon: <IconGlobalApp name={mapCommonIcons.delete} />,
      onClick: confirmDelete,
    },
  ]

  const dropDownButton =
    actionItems.length >= 1 && (isAdmin || isLeader) ? (
      <Dropdown
        placement="bottomRight"
        arrow
        trigger={['click']}
        menu={{ items: actionItems }}
      >
        <Button
          size={isSmallSize ? 'small' : 'middle'}
          icon={<IconGlobalApp name={mapCommonIcons.actionTable} />}
        />
      </Dropdown>
    ) : null

  useEffect(() => {
    handleCheckRole()
    getCurrentUser({})
  }, [])

  useEffect(() => {
    if (!projectId) return
    getDetail({ id: projectId })
    getMembers()
  }, [projectId])

  useEffect(() => {
    const result = (members ?? []).some(
      ({ userId, role }) => role === 'LEADER' && userId === currentUser?.id
    )
    setIsLeader(result)
  }, [members, currentUser])

  const memoChildren = useMemo(() => children, [children])

  const isSpinning =
    loadingCheckRole || loadingMember || loadingUser || (loading && !!detail)

  return (
    <Skeleton active loading={loading && !detail}>
      <Spin spinning={isSpinning} delay={200}>
        <Flex vertical style={{ marginTop: 16 }}>
          <Flex vertical gap={6}>
            <Row gutter={[12, 6]} wrap={false}>
              <Col flex="auto">
                <Title
                  level={isSmallSize ? 4 : 2}
                  style={{ margin: 0 }}
                  ellipsis={{ tooltip: true }}
                >
                  {detail?.title}
                </Title>
              </Col>

              <Col>
                <Flex gap={8}>
                  {memberButton}
                  {dropDownButton}
                </Flex>
              </Col>
            </Row>

            <Row gutter={[12, 6]} align="middle" wrap={false}>
              <Col flex="auto">
                <Text ellipsis={{ tooltip: true }}>{subInfoRender}</Text>
              </Col>

              <Col>
                <DetailMenuProject detailMenuItems={detailMenuItems} />
              </Col>
            </Row>
          </Flex>

          {memoChildren}
        </Flex>
      </Spin>

      {memberModal}

      {detail ? (
        <ModalEditProject
          editingItem={openEdit ? detail : undefined}
          showDepartment={!!detail.departmentId}
          onCancel={() => setOpenEdit(false)}
          onFinish={() => getDetail({ id: projectId })}
        />
      ) : null}
    </Skeleton>
  )
}

export default LayoutDetailProject
